import { SDF_DEFAULT_MARGIN_MM, SDF_ISO_THRESHOLD } from '../sdfConstants';
import { SDFOperations } from '../SDFOperations';
import { labelConnectedComponents } from '../SDFPostProcessing';
import { quantizeSdfMmToUint8 } from '../SDFPropagation';
import { maybeYield } from '../utils';
import {
    DEFAULT_COARSE_STRIDE,
    VariationalField,
} from './crossSectionFieldSampling';
import { evaluateVolume } from './crossSectionFieldSamplingPool';
import { fitVariationalField } from './crossSectionSurfaceFit';
import { polygonSetSdf2d } from './crossSectionGeometry';
import {
    planeSignedDistanceMm,
    projectToPlane,
} from '~/domain/geometry/patientGeometry';
import {
    PreparedSection,
    SectionGridSpec,
    computeSectionGridSpec,
    gridSampling,
    validateSections,
} from './crossSectionPreparation';
import {
    ConnectivityReport,
    CrossSectionError,
    CrossSectionInput,
    ReconstructionOptions,
    ReconstructionResult,
} from './crossSectionTypes';
import { Grid } from '~/engine/data/common/Grid';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';

const MARGIN_MM = SDF_DEFAULT_MARGIN_MM;
/** A slab's half thickness, in voxel footprints along the section normal. */
const SLAB_HALF_THICKNESS_VOXELS = 0.75;

function toBytes(mm: Float32Array, spec: SectionGridSpec): Uint8Array[] {
    const [nx, ny, nz] = spec.sizePx;
    return quantizeSdfMmToUint8(mm, nx, ny, nz, SDF_ISO_THRESHOLD, MARGIN_MM);
}

/** The fitted field as inside-high SDF bytes: sign and interface band are exact unless the coarse pass's first-order distance estimate overshoots, and interior magnitudes are not distances. */
export async function fieldToSdfBytes(
    field: VariationalField,
    spec: SectionGridSpec,
    imageGrid: Grid,
    signal?: AbortSignal
): Promise<Uint8Array[]> {
    const { origin, stepX, stepY, stepZ } = gridSampling(spec, imageGrid);
    const mmPosInside = await evaluateVolume(
        {
            field,
            sampling: { origin, stepX, stepY, stepZ },
            sizePx: [spec.sizePx[0], spec.sizePx[1], spec.sizePx[2]],
            stride: DEFAULT_COARSE_STRIDE,
            marginMm: MARGIN_MM,
        },
        signal
    );
    return toBytes(mmPosInside, spec);
}

/** The union of one section's drawn contours extruded through its plane's voxel footprint, as exact SDF bytes clamped to the margin. */
export async function slabToSdfBytes(
    section: PreparedSection,
    spec: SectionGridSpec,
    imageGrid: Grid,
    signal?: AbortSignal
): Promise<Uint8Array[]> {
    const [nx, ny, nz] = spec.sizePx;
    const { positionAt } = gridSampling(spec, imageGrid);
    const halfThicknessMm =
        SLAB_HALF_THICKNESS_VOXELS *
        imageGrid.voxelExtentAlong(section.frame.normal);
    const mmPosInside = new Float32Array(nx * ny * nz);
    let tick = performance.now();
    for (let k = 0; k < nz; k++) {
        for (let j = 0; j < ny; j++) {
            for (let i = 0; i < nx; i++) {
                const p = positionAt(i, j, k);
                const inPlane = polygonSetSdf2d(
                    section.polygons2d,
                    projectToPlane(section.frame, p)
                );
                const offPlane =
                    Math.abs(planeSignedDistanceMm(section.frame, p)) -
                    halfThicknessMm;
                mmPosInside[i + nx * (j + ny * k)] = Math.max(
                    -MARGIN_MM,
                    Math.min(MARGIN_MM, -Math.max(inPlane, offPlane))
                );
            }
        }
        tick = await maybeYield(tick, signal);
    }
    return toBytes(mmPosInside, spec);
}

export async function analyseConnectivity(
    bytesInsideHigh: Uint8Array[],
    spec: SectionGridSpec,
    voxelVolumeMm3: number,
    signal?: AbortSignal
): Promise<ConnectivityReport> {
    const { numComponents, componentSizes } = await labelConnectedComponents(
        bytesInsideHigh,
        spec.sizePx[0],
        spec.sizePx[1],
        spec.sizePx[2],
        SDF_ISO_THRESHOLD,
        true,
        signal
    );
    const volumes = Array.from(componentSizes)
        .filter((size) => size > 0)
        .map((size) => size * voxelVolumeMm3)
        .sort((a, b) => b - a);
    return { componentCount: numComponents, componentVolumesMm3: volumes };
}

/**
 * A structure from planar cross-sections as an SDF texture cropped to its content, which the caller owns: one plane extrudes as a slab, more are interpolated.
 * @throws CrossSectionError for unusable input, a cancelled fit, a busy GPU (`gl-busy`) or an empty result (`empty-result`).
 */
export async function reconstructFromSections(
    inputs: readonly CrossSectionInput[],
    imageGrid: Grid,
    options: ReconstructionOptions = {}
): Promise<ReconstructionResult> {
    const { signal } = options;
    const res = imageGrid.getRes();
    const sections = validateSections(inputs, imageGrid);
    const spec = computeSectionGridSpec(sections, imageGrid);
    const fit =
        sections.length === 1
            ? null
            : fitVariationalField(sections, {
                  residualToleranceMm: Math.min(res[0], res[1], res[2]) / 2,
                  signal,
              });
    const bytesInsideHigh = fit
        ? await fieldToSdfBytes(fit.field, spec, imageGrid, signal)
        : await slabToSdfBytes(sections[0], spec, imageGrid, signal);
    const connectivity = await analyseConnectivity(
        bytesInsideHigh,
        spec,
        res[0] * res[1] * res[2],
        signal
    );
    return {
        texture: await reinitializeOnGpu(bytesInsideHigh, spec, imageGrid),
        connectivity,
        maxResidualMm: fit?.maxResidualMm ?? 0,
        centreCount: fit?.centreCount ?? 0,
        toleranceMet: fit?.toleranceMet ?? true,
        isSlab: !fit,
    };
}

async function reinitializeOnGpu(
    bytesInsideHigh: Uint8Array[],
    spec: SectionGridSpec,
    imageGrid: Grid
): Promise<ContourTexture> {
    const composed = new ContourTexture(
        imageGrid,
        spec.sizePx,
        spec.originWorld
    );
    let reinitialized: ContourTexture | undefined;
    try {
        composed.resetSdfData(bytesInsideHigh);
        reinitialized = await SDFOperations.reinitializeSDFWebGL(
            composed,
            false
        );
    } finally {
        composed.delete();
    }
    if (!reinitialized) {
        throw new CrossSectionError(
            'gl-busy',
            'Another GPU operation is in progress — try again'
        );
    }
    try {
        if (reinitialized.isEmpty) {
            throw new CrossSectionError(
                'empty-result',
                'The cross-sections do not enclose a volume — the result is empty'
            );
        }
        reinitialized.cropToContentBounds();
    } catch (error) {
        reinitialized.delete();
        throw error;
    }
    return reinitialized;
}
